import React, { forwardRef } from 'react';
import Icon from './Icon';
import './Input.css';

/*
 * TextInput — the mock's `.input`.
 *   size     : 'md' (38px) | 'sm' (32px)
 *   multiline: render a <textarea class="input"> instead of <input>
 *   search   : wrap in the mock's `.search` (leading magnifier icon, padded). A
 *              clear "✕" appears at the right once there's text — click to clear
 *              (fires onClear if given, else onChange with an empty value).
 *   invalid  : red border (also set automatically by <Field error=…>)
 *   leftIcon : custom leading icon element (implies search-style left padding)
 * Controlled or uncontrolled; forwards ref to the underlying control.
 */
const TextInput = forwardRef(function TextInput({
  size = 'md',
  multiline = false,
  search = false,
  invalid = false,
  leftIcon = null,
  className = '',
  wrapClassName = '',
  rows,
  value,
  onChange,
  onClear,
  ...rest
}, ref) {
  const controlCls = [
    'input',
    size === 'sm' ? 'input--sm' : '',
    invalid ? 'input--invalid' : '',
    className,
  ].filter(Boolean).join(' ');

  const control = multiline
    ? <textarea ref={ref} className={controlCls} rows={rows} value={value} onChange={onChange} {...rest} />
    : <input ref={ref} className={controlCls} value={value} onChange={onChange} {...rest} />;

  const showSearchWrap = search || leftIcon;
  // clear affordance: only on search fields, only once there's text to clear.
  const canClear = search && value != null && value !== '';
  const handleClear = () => {
    if (onClear) onClear();
    else if (onChange) onChange({ target: { value: '' } });
  };

  if (showSearchWrap) {
    return (
      <div className={`search${canClear ? ' search--clearable' : ''}${wrapClassName ? ' ' + wrapClassName : ''}`}>
        {leftIcon != null ? leftIcon : <Icon name="search" size={15} />}
        {control}
        {canClear && (
          // preventDefault on mousedown keeps focus on the input through the click
          <button
            type="button"
            className="search__clear"
            aria-label="Clear search"
            title="Clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
          >
            <Icon name="close" size={14} />
          </button>
        )}
      </div>
    );
  }
  return control;
});

export default TextInput;
